import { Fragment, useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'
import { BadgeCheck, Copy, Download, Eye, ChevronDown, ChevronRight, PackageCheck, Printer, QrCode, RotateCcw, Route, Trash2, Truck, XCircle, UserPlus, UserCheck } from 'lucide-react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import { Skeleton } from '@/components/ui/skeleton'
import { OrderStatusBadge } from '@/pages/supplier/components/OrderStatusBadge'
import { OrderFlagBadges } from './OrderFlagBadges'
import type { EmployeeManualOrder } from '@/api/employee-manual-order'
import { REFUND_TYPE } from '@/schemas/supplier'
import type { OrderCartInfo } from '@/schemas/supplier'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'

interface ManualOrderTableProps {
  orders: EmployeeManualOrder[]
  isLoading?: boolean
  onViewDetail?: (order: EmployeeManualOrder) => void
  onManualPayment?: (order: EmployeeManualOrder) => void
  onConfirmPayment?: (order: EmployeeManualOrder) => void
  onCancel?: (order: EmployeeManualOrder) => void
  onApplyRefund?: (order: EmployeeManualOrder) => void
  onDelivery?: (order: EmployeeManualOrder) => void
  onViewTracking?: (order: EmployeeManualOrder) => void
  onMarkReturned?: (order: EmployeeManualOrder) => void
  onDelete?: (order: EmployeeManualOrder) => void
  onBatchDelete?: (ids: number[]) => void
  onSelectedExport?: (orderIds: number[]) => void
  onBatchPrint?: (orderIds: number[]) => void
  canConfirmPayment?: boolean
  canCancel?: boolean
  canApplyRefund?: boolean
  canDelivery?: boolean
  canMarkRefundReturned?: boolean
  canDelete?: boolean
}

const PAY_TYPE_LABELS: Record<string, string> = {
  weixin: '微信支付',
  wxpay: '微信支付',
  wechat: '微信支付',
  yue: '余额支付',
  balance: '余额支付',
  offline: '线下支付',
  alipay: '支付宝',
}

function getPayTypeLabel(order: EmployeeManualOrder) {
  if (order.pay_type) {
    return PAY_TYPE_LABELS[order.pay_type] || order.pay_type
  }
  return order.paid === 1 ? '已支付' : '未支付'
}

export function ManualOrderTable({
  orders,
  isLoading,
  onViewDetail,
  onManualPayment,
  onConfirmPayment,
  onCancel,
  onApplyRefund,
  onDelivery,
  onViewTracking,
  onMarkReturned,
  onDelete,
  onBatchDelete,
  onSelectedExport,
  onBatchPrint,
  canConfirmPayment = false,
  canCancel = false,
  canApplyRefund = false,
  canDelivery = false,
  canMarkRefundReturned = false,
  canDelete = false,
}: ManualOrderTableProps) {
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set())
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set())
  const currentPageIds = useMemo(() => orders.map((order) => order.id), [orders])
  const allSelected = currentPageIds.length > 0 && currentPageIds.every((id) => selectedRows.has(id))
  const someSelected = currentPageIds.some((id) => selectedRows.has(id))

  useEffect(() => {
    setSelectedRows((prev) => {
      const visibleIds = new Set(currentPageIds)
      const next = new Set(Array.from(prev).filter((id) => visibleIds.has(id)))
      return next.size === prev.size ? prev : next
    })
  }, [currentPageIds])

  const toggleExpand = (id: number) => {
    const newExpanded = new Set(expandedRows)
    if (newExpanded.has(id)) {
      newExpanded.delete(id)
    } else {
      newExpanded.add(id)
    }
    setExpandedRows(newExpanded)
  }

  const handleSelectAll = (checked: boolean | 'indeterminate') => {
    setSelectedRows((prev) => {
      const next = new Set(prev)
      if (checked) {
        currentPageIds.forEach((id) => next.add(id))
      } else {
        currentPageIds.forEach((id) => next.delete(id))
      }
      return next
    })
  }

  const handleSelectRow = (id: number, checked: boolean | 'indeterminate') => {
    setSelectedRows((prev) => {
      const next = new Set(prev)
      if (checked) {
        next.add(id)
      } else {
        next.delete(id)
      }
      return next
    })
  }

  const handleBatchPrint = () => {
    onBatchPrint?.(orders.filter((order) => selectedRows.has(order.id)).map((order) => order.id))
  }

  const handleSelectedExport = () => {
    onSelectedExport?.(orders.filter((order) => selectedRows.has(order.id)).map((order) => order.id))
  }

  const handleBatchDelete = () => {
    onBatchDelete?.(orders.filter((order) => selectedRows.has(order.id)).map((order) => order.id))
  }

  // 复制收货人信息：收货人，手机号，地址
  const handleCopyReceiver = async (order: EmployeeManualOrder) => {
    const text = [order.real_name, order.user_phone, order.user_address].filter(Boolean).join(',')
    try {
      await navigator.clipboard.writeText(text)
    } catch {
      // 剪贴板不可用时降级（非 https 环境）
      const textarea = document.createElement('textarea')
      textarea.value = text
      document.body.appendChild(textarea)
      textarea.select()
      document.execCommand('copy')
      document.body.removeChild(textarea)
    }
    toast.success('已复制收货人信息', { description: text })
  }

  const calculateCartTotal = (cartInfo: OrderCartInfo[] | undefined) => {
    if (!cartInfo || cartInfo.length === 0) return 0
    return cartInfo.reduce((sum, item) => sum + item.total_price, 0)
  }

  if (isLoading) {
    return (
      <div className="rounded-xl border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12"><Skeleton className="h-4 w-4" /></TableHead>
              <TableHead className="w-8"><Skeleton className="h-4 w-4" /></TableHead>
              <TableHead className="w-52"><Skeleton className="h-4 w-28" /></TableHead>
              <TableHead><Skeleton className="h-4 w-24" /></TableHead>
              <TableHead><Skeleton className="h-4 w-32" /></TableHead>
              <TableHead className="text-right"><Skeleton className="h-4 w-16 ml-auto" /></TableHead>
              <TableHead className="w-24"><Skeleton className="h-4 w-16" /></TableHead>
              <TableHead className="w-24"><Skeleton className="h-4 w-16" /></TableHead>
              <TableHead className="w-24"><Skeleton className="h-4 w-16" /></TableHead>
              <TableHead className="w-40"><Skeleton className="h-4 w-24" /></TableHead>
              <TableHead className="w-40"><Skeleton className="h-4 w-24" /></TableHead>
              <TableHead className="w-56 text-center"><Skeleton className="h-4 w-16 mx-auto" /></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {Array.from({ length: 5 }).map((_, index) => (
              <TableRow key={index}>
                <TableCell><Skeleton className="h-4 w-4" /></TableCell>
                <TableCell><Skeleton className="h-4 w-4" /></TableCell>
                <TableCell><Skeleton className="h-4 w-28" /></TableCell>
                <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                <TableCell><Skeleton className="h-4 w-32" /></TableCell>
                <TableCell><Skeleton className="h-4 w-16 ml-auto" /></TableCell>
                <TableCell><Skeleton className="h-6 w-16" /></TableCell>
                <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                <TableCell><Skeleton className="h-8 w-48 mx-auto" /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    )
  }

  if (orders.length === 0) {
    return (
      <div className="rounded-xl border bg-card py-12 text-center">
        <p className="text-muted-foreground">暂无手动订单数据</p>
      </div>
    )
  }

  return (
    <div className="rounded-xl border bg-card">
      {selectedRows.size > 0 && (
        <div className="flex items-center justify-between gap-3 border-b px-4 py-3">
          <span className="text-sm text-muted-foreground">已选择 {selectedRows.size} 项</span>
          <div className="flex items-center gap-2">
            {onSelectedExport && (
              <Button variant="outline" size="sm" onClick={handleSelectedExport}>
                <Download className="mr-1.5 h-3.5 w-3.5" />
                按勾选导出
              </Button>
            )}
            {onBatchDelete && canDelete && (
              <Button variant="outline" size="sm" className="text-destructive hover:text-destructive" onClick={handleBatchDelete}>
                <Trash2 className="mr-1.5 h-3.5 w-3.5" />
                批量删除
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={handleBatchPrint}>
              <Printer className="mr-1.5 h-3.5 w-3.5" />
              批量打印清单
            </Button>
          </div>
        </div>
      )}
      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead className="w-12">
              <Checkbox
                checked={someSelected && !allSelected ? 'indeterminate' : allSelected}
                onCheckedChange={handleSelectAll}
                aria-label="全选"
              />
            </TableHead>
            <TableHead className="w-8" />
            <TableHead className="w-52">订单号</TableHead>
            <TableHead>收货人</TableHead>
            <TableHead className="min-w-28">创建人</TableHead>
            <TableHead>商品</TableHead>
            <TableHead className="text-right">金额</TableHead>
            <TableHead className="w-24">状态</TableHead>
            <TableHead className="w-28">支付方式</TableHead>
            <TableHead className="w-24">订单类型</TableHead>
            <TableHead className="w-40">发货备注</TableHead>
            <TableHead className="w-40">创建时间</TableHead>
            <TableHead className="w-56 text-center">操作</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {orders.map((order) => {
            const isExpanded = expandedRows.has(order.id)
            const cartInfo = order.cart_info || []
            const cartTotal = calculateCartTotal(cartInfo)
            const canManualPay = order.status === -1 && order.paid === 0
            const canShowRefund = canApplyRefund && order.paid === 1
            const canShowDelivery =
              canDelivery && order.paid === 1 && order.status === 0 && (order.refund_type ?? REFUND_TYPE.NONE) === REFUND_TYPE.NONE
            const canShowTracking = !!order.delivery_id && (order.status === 1 || order.status === 2)
            const canShowMarkReturned =
              canMarkRefundReturned &&
              order.paid === 1 &&
              order.refund_type === REFUND_TYPE.PENDING_RETURN
            const canShowDelete =
              canDelete &&
              (order.status === -2 || order.status === 2 || order.status === 3 || order.refund_type === 6)

            return (
              <Fragment key={order.id}>
                <TableRow className="group">
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      checked={selectedRows.has(order.id)}
                      onCheckedChange={(checked) => handleSelectRow(order.id, checked)}
                      aria-label={`选择订单 ${order.order_id}`}
                    />
                  </TableCell>
                  <TableCell>
                    <button
                      onClick={() => toggleExpand(order.id)}
                      className="flex items-center justify-center w-6 h-6 rounded hover:bg-muted transition-colors"
                      aria-label={isExpanded ? '收起' : '展开'}
                    >
                      {isExpanded ? (
                        <ChevronDown className="h-4 w-4 text-muted-foreground" />
                      ) : (
                        <ChevronRight className="h-4 w-4 text-muted-foreground" />
                      )}
                    </button>
                  </TableCell>
                  <TableCell>
                    <div>
                      <span className={`font-mono text-sm ${(order.is_store || order.is_cash) ? 'text-red-500' : ''}`}>
                        {order.order_id}
                      </span>
                      <OrderFlagBadges order={order} />
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-start gap-1">
                      <div className="min-w-0">
                        <p className="text-sm font-medium">{order.real_name}</p>
                        <p className="text-xs text-muted-foreground">{order.user_phone}</p>
                      </div>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              onClick={() => handleCopyReceiver(order)}
                              className="flex h-6 w-6 shrink-0 items-center justify-center rounded hover:bg-muted transition-colors"
                              aria-label={`复制收货人信息 ${order.order_id}`}
                            >
                              <Copy className="h-3.5 w-3.5 text-muted-foreground" />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent side="top">
                            复制：收货人，手机号，地址
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                  </TableCell>
                  <TableCell>
                    <p className="text-sm">{order.creator_name || '-'}</p>
                  </TableCell>
                  <TableCell>
                    <div className="max-w-[200px]">
                      {cartInfo.length > 0 && (
                        <>
                          <p className="text-sm truncate">{cartInfo[0].product_name}</p>
                          {cartInfo.length > 1 && (
                            <p className="text-xs text-muted-foreground">
                              +{cartInfo.length - 1} 件商品
                            </p>
                          )}
                        </>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <span className="font-semibold">¥{order.pay_price.toFixed(2)}</span>
                  </TableCell>
                  <TableCell>
                    <OrderStatusBadge status={order.status} refundType={order.refund_type} />
                  </TableCell>
                  <TableCell>
                    <span className="text-sm">{getPayTypeLabel(order)}</span>
                  </TableCell>
                  <TableCell>
                    {order.is_new === 1 ? (
                      <Badge variant="secondary" className="bg-green-50 text-green-700 hover:bg-green-50">
                        <UserPlus className="mr-1 h-3 w-3" />
                        新开
                      </Badge>
                    ) : order.is_new === 0 ? (
                      <Badge variant="secondary" className="bg-orange-50 text-orange-700 hover:bg-orange-50">
                        <UserCheck className="mr-1 h-3 w-3" />
                        复购
                      </Badge>
                    ) : (
                      <span className="text-sm text-muted-foreground">-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="text-sm text-muted-foreground line-clamp-2 max-w-[180px] inline-block text-left">
                            {order.shipping_remark || '-'}
                          </span>
                        </TooltipTrigger>
                        {order.shipping_remark && (
                          <TooltipContent side="top" className="max-w-sm">
                            {order.shipping_remark}
                          </TooltipContent>
                        )}
                      </Tooltip>
                    </TooltipProvider>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm text-muted-foreground">{order.add_time}</span>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap items-center justify-center gap-1">
                      {canManualPay && (
                        <Button variant="ghost" size="sm" onClick={() => onManualPayment?.(order)}>
                          <QrCode className="mr-1.5 h-3.5 w-3.5" />
                          手动支付
                        </Button>
                      )}
                      {canManualPay && canConfirmPayment && (
                        <Button variant="ghost" size="sm" onClick={() => onConfirmPayment?.(order)}>
                          <BadgeCheck className="mr-1.5 h-3.5 w-3.5" />
                          确认收款
                        </Button>
                      )}
                      {canManualPay && canCancel && (
                        <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive" onClick={() => onCancel?.(order)}>
                          <XCircle className="mr-1.5 h-3.5 w-3.5" />
                          取消订单
                        </Button>
                      )}
                      {canShowRefund && (
                        <Button variant="ghost" size="sm" onClick={() => onApplyRefund?.(order)}>
                          <RotateCcw className="mr-1.5 h-3.5 w-3.5" />
                          售后
                        </Button>
                      )}
                      {canShowMarkReturned && (
                        <Button variant="ghost" size="sm" onClick={() => onMarkReturned?.(order)}>
                          <PackageCheck className="mr-1.5 h-3.5 w-3.5" />
                          登记退货
                        </Button>
                      )}
                      {canShowDelivery && (
                        <Button variant="ghost" size="sm" onClick={() => onDelivery?.(order)}>
                          <Truck className="mr-1.5 h-3.5 w-3.5" />
                          发货
                        </Button>
                      )}
                      {canShowTracking && (
                        <Button variant="ghost" size="sm" onClick={() => onViewTracking?.(order)}>
                          <Route className="mr-1.5 h-3.5 w-3.5" />
                          物流
                        </Button>
                      )}
                      {canShowDelete && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive hover:text-destructive"
                          onClick={() => onDelete?.(order)}
                        >
                          <Trash2 className="mr-1.5 h-3.5 w-3.5" />
                          删除
                        </Button>
                      )}
                      <Button variant="ghost" size="sm" onClick={() => onViewDetail?.(order)}>
                        <Eye className="mr-1.5 h-3.5 w-3.5" />
                        详情
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>

                {isExpanded && (
                  <TableRow className="bg-muted/30 hover:bg-muted/40">
                    <TableCell colSpan={13} className="p-0">
                      <div className="px-12 py-4">
                        <div className="space-y-3">
                          {cartInfo.length > 0 ? (
                            cartInfo.map((item, index) => (
                              <div
                                key={item.cart_id || index}
                                className="flex items-center gap-4 p-3 bg-background rounded-lg border"
                              >
                                <div className="w-16 h-16 rounded-md overflow-hidden bg-muted flex-shrink-0">
                                  {item.product_image ? (
                                    <img
                                      src={item.product_image}
                                      alt={item.product_name}
                                      className="w-full h-full object-cover"
                                    />
                                  ) : (
                                    <div className="w-full h-full flex items-center justify-center text-muted-foreground text-xs">
                                      暂无图片
                                    </div>
                                  )}
                                </div>

                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium truncate">{item.product_name}</p>
                                  {item.product_attr && (
                                    <p className="text-xs text-muted-foreground mt-0.5">
                                      规格：{item.product_attr}
                                    </p>
                                  )}
                                  {item.sku && (
                                    <p className="text-xs text-muted-foreground">SKU：{item.sku}</p>
                                  )}
                                </div>

                                <div className="text-right w-24">
                                  <p className="text-sm text-muted-foreground">单价</p>
                                  <p className="text-sm font-medium">¥{item.price.toFixed(2)}</p>
                                </div>

                                <div className="text-right w-16">
                                  <p className="text-sm text-muted-foreground">数量</p>
                                  <p className="text-sm font-medium">×{item.cart_num}</p>
                                </div>

                                <div className="text-right w-28">
                                  <p className="text-sm text-muted-foreground">小计</p>
                                  <p className="text-sm font-semibold text-primary">
                                    ¥{item.total_price.toFixed(2)}
                                  </p>
                                </div>
                              </div>
                            ))
                          ) : (
                            <div className="text-center py-4 text-muted-foreground text-sm">
                              暂无商品信息
                            </div>
                          )}
                        </div>

                        {cartInfo.length > 0 && (
                          <div className="flex justify-end items-center gap-6 mt-4 pt-4 border-t">
                            <div className="text-sm text-muted-foreground">
                              共 <span className="font-medium text-foreground">{order.total_num}</span> 件商品
                            </div>
                            <div className="text-sm text-muted-foreground">
                              商品总价：<span className="font-medium text-foreground">¥{cartTotal.toFixed(2)}</span>
                            </div>
                            <div className="text-sm">
                              实付金额：<span className="font-semibold text-lg text-primary">¥{order.pay_price.toFixed(2)}</span>
                            </div>
                          </div>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </Fragment>
            )
          })}
        </TableBody>
      </Table>
    </div>
  )
}
